import React from "react";
import { Sparkles } from "lucide-react";
import { Badge } from "../../components/primitives";
import { Shimmer } from "../../components/StatCard";
import { SplitBar } from "../../components/drilldown/charts";
import { fmtMins } from "../../components/drilldown/sourceUi";

/**
 * The frame the drill-down pages share.
 *
 * These are the drawer's own section chrome — its labels, its metric header,
 * its read card — lifted out so a person page and a project page look like the
 * same product, and so neither one re-invents spacing the other already chose.
 */

/* The digest is two columns on a wide screen and one on a narrow one. Inline
   styles cannot express a media query, so the rule ships once here. */
const GRID_CSS = `
.tg-drill-grid { grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr); }
@media (max-width: 1100px) {
  .tg-drill-grid { grid-template-columns: minmax(0, 1fr); }
  .tg-drill-rail { position: static !important; }
}
`;

export function DrilldownStyles() {
  return <style>{GRID_CSS}</style>;
}

/** Breadcrumb slot — pinned above the content, with the page's own rhythm. */
export function Crumbs({ children }) {
  return (
    <>
      <DrilldownStyles />
      <div style={{ marginBottom: 16, minWidth: 0 }}>{children}</div>
    </>
  );
}

export function SecLabel({ t, hint, children }) {
  return (
    <div style={{
      display: "flex", alignItems: "baseline", justifyContent: "space-between",
      gap: 10, marginBottom: 9,
    }}>
      <span style={{
        fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase",
        color: t.c.textTertiary,
      }}>{children}</span>
      {hint && <span style={{ fontSize: 11, color: t.c.textTertiary, textAlign: "right" }}>{hint}</span>}
    </div>
  );
}

export function Section({ t, title, hint, children }) {
  return (
    <section style={{ minWidth: 0 }}>
      {title && <SecLabel t={t} hint={hint}>{title}</SecLabel>}
      {children}
    </section>
  );
}

/** The context column. Sticky on wide screens so it stays with a long log. */
/**
 * ColLabel — the eyebrow over each half of a layer-2 page.
 *
 * Layer 2 is half picture, half key things. Saying so out loud costs one line
 * and stops the right-hand column being read as leftovers.
 */
export function ColLabel({ t, children }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      fontSize: 10, fontWeight: 700, letterSpacing: 1.15, textTransform: "uppercase",
      color: t.c.textTertiary, margin: "2px 2px 0",
    }}>
      {children}
      <span style={{ flex: 1, height: 1, background: `linear-gradient(to right, ${t.c.border}, transparent)` }} />
    </div>
  );
}

export function Rail({ t, children }) {
  return (
    <aside
      className="tg-drill-rail"
      style={{
        display: "flex", flexDirection: "column", gap: t.s[5], minWidth: 0,
        position: "sticky", top: 0,
      }}
    >
      <ColLabel t={t}>Key things</ColLabel>
      {children}
    </aside>
  );
}

/** Hours at a glance: the figure, the period delta, and the active/idle split. */
export function MetricHeader({ t, label, value, delta, activeMinutes, idleMinutes, children }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "1fr auto", alignItems: "end", gap: 10,
      padding: t.s[4], borderRadius: t.r.md,
      background: t.c.surface, boxShadow: `0 0 0 1px ${t.c.border}`,
    }}>
      <div>
        <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginBottom: 5 }}>{label}</div>
        {value == null ? (
          <Shimmer w={140} h={28} />
        ) : (
          <div style={{
            fontSize: 30, fontWeight: 600, letterSpacing: "-0.8px",
            fontVariantNumeric: "tabular-nums", lineHeight: 1, color: t.c.text,
          }}>{value}</div>
        )}
      </div>
      {Number.isFinite(delta) && (
        <Badge tone={delta >= 0 ? "positive" : "negative"}>
          {delta >= 0 ? "▲" : "▼"} {Math.abs(Math.round(delta))}%
        </Badge>
      )}

      <div style={{ gridColumn: "1/-1", marginTop: 12 }}>
        <SplitBar activeMinutes={activeMinutes} idleMinutes={idleMinutes} height={6} />
      </div>
      <div style={{
        gridColumn: "1/-1", display: "flex", gap: 14, fontSize: 11,
        color: t.c.textTertiary, marginTop: 7, flexWrap: "wrap",
      }}>
        <span><Dot c={t.status.positive} />Active <b style={{ color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{fmtMins(activeMinutes)}</b></span>
        <span><Dot c={t.status.warning} />Idle <b style={{ color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{fmtMins(idleMinutes)}</b></span>
      </div>

      {children && <div style={{ gridColumn: "1/-1", marginTop: 12 }}>{children}</div>}
    </div>
  );
}

/**
 * ScopeBar — the promise that nothing outside this drill is on the page.
 *
 * A drilled page is only trustworthy if the reader can tell what it excludes.
 * Without this the trail says "Development tools › Visual Studio Code" while a
 * panel underneath quietly draws organisation-wide hours, and the two disagree
 * with nobody noticing. So each page states its own boundary in words.
 *
 *   subject   what the page is limited to, in reading order, outermost first
 *             e.g. ["the Development tools category", "Visual Studio Code"]
 *   excludes  what is deliberately NOT here, phrased from the reader's side
 *             e.g. ["every other tool", "the other 21 people"]
 *   coverage  optional honest gap, e.g. "based on activity detail from 14 of 22"
 *
 * Renders nothing when there is no subject — the dashboard itself is not a
 * drill and does not need a boundary statement.
 */
export function ScopeBar({ t, subject = [], excludes = [], coverage }) {
  const subj = subject.filter(Boolean);
  if (!subj.length) return null;
  const ex = excludes.filter(Boolean);
  return (
    <div
      style={{
        display: "flex", gap: 11, alignItems: "flex-start",
        padding: `${t.s[3]}px ${t.s[4]}px`, borderRadius: t.r.md,
        background: t.c.primarySoft,
        boxShadow: `0 0 0 1px ${t.c.primary}33`,
        fontSize: 12, lineHeight: 1.6, color: t.c.textSecondary,
      }}
    >
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"
        style={{ width: 15, height: 15, flexShrink: 0, color: t.c.primary, marginTop: 1 }}>
        <path d="M8 1.5 2 4v3.6c0 3.4 2.4 6.5 6 6.9 3.6-.4 6-3.5 6-6.9V4L8 1.5Z"
          stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        <path d="M5.6 8 7.3 9.7l3.2-3.4" stroke="currentColor" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span>
        Every figure on this page is limited to{" "}
        {subj.map((x, i) => (
          <React.Fragment key={x}>
            {i > 0 && <span style={{ color: t.c.textTertiary }}> › </span>}
            <b style={{ color: t.c.text, fontWeight: 600 }}>{x}</b>
          </React.Fragment>
        ))}
        .
        {ex.length > 0 && (
          <span style={{ color: t.c.textTertiary }}>
            {" "}Excluded: {ex.join(", ")} — nothing outside this drill appears here.
          </span>
        )}
        {coverage && (
          <span style={{ color: t.c.textTertiary }}> {coverage}</span>
        )}
      </span>
    </div>
  );
}

export function Dot({ c }) {
  return <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 99, background: c, marginRight: 6 }} />;
}

/**
 * The read. Marked "computed" when it was derived in code rather than by a
 * model — the reader is entitled to know which one they are looking at.
 */
export function ReadCard({ t, read, readErr }) {
  return (
    <div style={{
      position: "relative", padding: `${t.s[4]}px ${t.s[4]}px ${t.s[4]}px 17px`,
      borderRadius: t.r.md, background: t.c.aiSoft, boxShadow: `0 0 0 1px ${t.c.aiBorder}`,
    }}>
      <span style={{ position: "absolute", left: 0, top: 10, bottom: 10, width: 2, borderRadius: 2, background: t.c.ai }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 9 }}>
        <span style={{
          display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600,
          letterSpacing: 0.5, textTransform: "uppercase", color: t.c.ai,
        }}>
          <Sparkles size={12} /> The read
        </span>
        {read?.deterministic && <span style={{ fontSize: 10, color: t.c.textTertiary }}>computed</span>}
      </div>
      {read?.sentences?.length ? (
        read.sentences.map((s, i) => (
          <p key={i} style={{
            margin: `0 0 ${i === read.sentences.length - 1 ? 0 : 9}px`,
            fontSize: 13.5, lineHeight: 1.6, color: t.c.text,
          }}>{s}</p>
        ))
      ) : readErr ? (
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: t.c.textTertiary }}>{readErr}</p>
      ) : (
        <>
          <Shimmer w="100%" h={12} /><div style={{ height: 7 }} />
          <Shimmer w="92%" h={12} /><div style={{ height: 7 }} />
          <Shimmer w="76%" h={12} />
        </>
      )}
    </div>
  );
}

export function sevColor(t, sev) {
  if (sev === "negative") return t.status.negative;
  if (sev === "warning") return t.status.warning;
  if (sev === "positive") return t.status.positive;
  return t.status.info;
}

/**
 * One takeaway. A button when it leads somewhere, a plain panel when it is the
 * thing being read — the same card either way, so opening one does not feel
 * like arriving at a different component.
 */
export function TakeawayCard({ t, takeaway, onClick, active = false }) {
  const body = (
    <>
      <span style={{ width: 3, borderRadius: 2, flexShrink: 0, background: sevColor(t, takeaway.sev) }} />
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{
          display: "block", fontSize: active ? 15 : 13, fontWeight: active ? 600 : 500,
          lineHeight: 1.4, color: t.c.text,
        }}>{takeaway.title}</span>
        {takeaway.detail && (
          <span style={{
            display: "block", fontSize: active ? 13 : 12, color: t.c.textTertiary,
            lineHeight: 1.55, marginTop: active ? 6 : 3,
          }}>{takeaway.detail}</span>
        )}
      </span>
    </>
  );

  const style = {
    display: "flex", gap: 10, padding: active ? "14px 16px" : "10px 12px",
    borderRadius: t.r.md, background: t.c.surface,
    boxShadow: `0 0 0 1px ${t.c.border}`, textAlign: "left",
    fontFamily: t.font.family, width: "100%",
  };

  if (!onClick) return <div style={style}>{body}</div>;

  return (
    <button
      onClick={onClick}
      title="Open the detail behind this"
      style={{ ...style, border: "none", cursor: "pointer" }}
    >
      {body}
    </button>
  );
}

/** Label/value rows — the figures a claim was computed from. */
export function KV({ t, items }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {items.filter(Boolean).map(([k, v]) => (
        <div key={k} style={{
          display: "flex", alignItems: "baseline", justifyContent: "space-between",
          gap: 12, padding: "9px 2px", borderBottom: `1px solid ${t.c.border}`,
        }}>
          <span style={{ fontSize: 12.5, color: t.c.textSecondary }}>{k}</span>
          <span style={{
            fontSize: 13, fontWeight: 500, color: t.c.text, fontVariantNumeric: "tabular-nums",
          }}>{v}</span>
        </div>
      ))}
    </div>
  );
}
